const OS_WINDOW_SHELL_BASE =
  "rounded-3xl shadow-lg group/window overflow-visible border border-slate-200/80 shadow-black/10 dark:border-white/10 dark:shadow-black/20";

const OS_WINDOW_SHELL_GLASS =
  `bg-gradient-to-bl backdrop-blur from-white/90 to-white/80 dark:from-slate-900/70 dark:to-slate-900/80 ${OS_WINDOW_SHELL_BASE}`;

const OS_WINDOW_SHELL_OPAQUE = `bg-white dark:bg-slate-900 ${OS_WINDOW_SHELL_BASE}`;

/**
 * macOS-inspired window controls — circle 14px, icon 9px, gap 7px.
 * Hover fills indigo; glyphs reveal on cluster hover (`group/cwc`), not per-button.
 * Hover cluster: `-m-1 p-1` forgives the pointer by 4px on every side.
 */
export const OS_WINDOW_TITLEBAR_BUTTONS_CLASS =
  "group/cwc flex items-center gap-[7px] self-center -m-1 p-1 pr-4";

export const OS_WINDOW_TITLEBAR_BUTTON_CLASS =
  "group/wc relative grid size-[14px] shrink-0 cursor-pointer place-items-center rounded-full border-0 bg-slate-300/90 p-0 outline-none transition-colors hover:bg-indigo-500 focus-visible:bg-indigo-500 dark:bg-white/28 dark:hover:bg-indigo-500 dark:focus-visible:bg-indigo-500 appearance-none";

/**
 * High-contrast glyph colour — slate-900 on the resting grey circle, white on
 * the indigo hover fill. Always visible (`opacity-100`).
 */
export const OS_WINDOW_TITLEBAR_BUTTON_ICON_CLASS =
  "pointer-events-none absolute inset-0 h-full w-full text-slate-900 opacity-100 transition-colors group-hover/wc:text-white group-focus-visible/wc:text-white dark:text-white";

/** Window frame classes — frosted glass when focused, solid when not. */
export function getOsWindowShellClassName(isFocused = true): string {
  if (isFocused) {
    return OS_WINDOW_SHELL_GLASS;
  }
  return OS_WINDOW_SHELL_OPAQUE;
}

/** Dim overlay shown when window content is suspended in a background workspace. */
export function getOsWindowSuspendedOverlayClassName(): string {
  return "absolute inset-0 bg-slate-200/40 dark:bg-slate-900/30 backdrop-blur-[2px] flex items-center justify-center pointer-events-none z-50";
}

/**
 * Frosted-glass background classes for dock, popovers, and other OS chrome surfaces.
 */
export function getOsGlassSurfaceClassName(glassClasses: string): string {
  return glassClasses;
}

/**
 * Docked Ani gutter — must match sidebar shell so the seam stays clean when chat is open.
 */
export const OS_ANI_DOCK_GUTTER_CLASS = "bg-white dark:bg-slate-900";

/**
 * Docked Ani sidebar shell.
 * Light: opaque white (no bleed-through from desktop gradient).
 * Dark: solid slate-900 — matches unfocused OS window surfaces.
 */
export const OS_ANI_DOCK_SIDEBAR_SHELL_CLASS = "bg-white dark:bg-slate-900";

/** Inner docked panels — inherit shell in dark (do not paint a darker layer over it). */
export const OS_ANI_DOCK_SIDEBAR_INNER_CLASS = "bg-white dark:bg-transparent";

/**
 * Soft seam shadow cast from the main OS column onto the docked chat panel.
 * Gradient overlay on the chat shell — reads on white and dark, same both modes.
 */
export const OS_ANI_DOCK_CHAT_EDGE_SHADOW_CLASS =
  "after:pointer-events-none after:absolute after:inset-y-0 after:left-0 after:z-20 after:w-5 after:bg-gradient-to-r after:from-black/10 after:to-transparent after:content-['']";

/**
 * Floating Ani pop-out shell.
 * Light: opaque white card. Dark: original frosted glass (50–60% slate-900 + blur).
 */
export const OS_ANI_FLOATING_SHELL_CLASS =
  "bg-gradient-to-bl backdrop-blur-xl backdrop-saturate-150 border border-slate-200 from-white to-slate-50/95 dark:border-white/10 dark:from-slate-900/50 dark:to-slate-900/60";

/**
 * Ani composer inset — light: white card; dark: original ChatInput default (slate-800/90).
 */
export const OS_ANI_SIDEBAR_INPUT_CLASS =
  "border border-slate-200 bg-white shadow-sm dark:border-transparent dark:bg-slate-800/90";
